import { useState } from 'react';
import { Typography, Popover, Box, Stack, Link, Backdrop, StorefrontOutlinedIcon, ExpandMoreIcon,
    WorkspacePremiumOutlinedIcon, EmailOutlinedIcon, customTheme, Loader
 } from 'src/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, createStripePortalSession } from 'src/utils';
import { useSnackbar } from 'src/hooks';

export default function DesktopHeaderMenu({ isHomePage, isHandbook }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [loadingStripePortal, setLoadingStripePortal] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { openSnackbar } = useSnackbar();

    const userHasSubscription = user && user.membership !== "Free";
    const open = Boolean(anchorEl);

    const isMembershipsEnabled = import.meta.env.VITE_FEATURE_MEMBERSHIPS === 'true';

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigation = (path) => {
        navigate(path);
        handleClose();
    };

    const linkColor = (isHomePage || isHandbook) ? '#fff' : customTheme.palette.text.main;
    const hoverColor = (isHomePage || isHandbook) ? '#fff' : customTheme.palette.primary.main;

    const handleManageSubscriptionClick = async () => {
        handleClose();

        if (!userHasSubscription) {
            handleNavigation('/memberships');
            return;
        }

        setLoadingStripePortal(true);
        try {
            const { url } = await createStripePortalSession(user.id);
            window.open(url, "_blank");
        } catch (err) {
            console.error("Failed to open subscription portal", err);
            openSnackbar("Unable to open subscription management right now.", "error");
        } finally {
            setLoadingStripePortal(false);
        }
    };

    const menuItems = [
        { 
            label: userHasSubscription ? 'Subscription' : 'Membership', 
            action: handleManageSubscriptionClick,
            icon: <WorkspacePremiumOutlinedIcon sx={{color: customTheme.palette.text.secondary, fontSize: 20}}/>,
            disabled: !isMembershipsEnabled
        },
        { 
            label: 'PanAm Shop', 
            path: '/shop', 
            icon: <StorefrontOutlinedIcon sx={{color: customTheme.palette.text.secondary, fontSize: 20}} />,
            disabled: true
        },
        { 
            label: 'Contact Us', 
            path: '/contact-us', 
            icon: <EmailOutlinedIcon sx={{color: customTheme.palette.text.secondary, fontSize: 20}} />, 
            //external: true 
        }
    ];

    return (
        <>
            <Box sx={{ position: 'relative' }}>
                <Link
                    underline="none"
                    onClick={handleOpen}
                    sx={{
                        color: linkColor,
                        px: 1,
                        py: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                            color: hoverColor,
                        },
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600}>
                        More
                    </Typography>
                    <ExpandMoreIcon fontSize="small" />
                </Link>

                {open && (
                    <Backdrop
                        open
                        onClick={handleClose}
                        sx={{
                            zIndex: 1100,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        }}
                    />
                )}

                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            p: 0.5,
                            backgroundColor: '#fff',
                            color: customTheme.palette.text.main,
                            borderRadius: 2,
                            boxShadow: 4,
                            minWidth: 160,
                        },
                    }}
                >
                <Stack>
                    {menuItems.map((item) => {
                        const isActive = location.pathname.includes(item.path);
                        const textColor = isActive
                            ? customTheme.palette.primary.main
                            : customTheme.palette.text.secondary;

                    const content = (
                        <Box
                            sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 0.5,
                            py: 1,
                            borderRadius: 2,
                            cursor: item.disabled ? 'default' : 'pointer',
                            opacity: item.disabled ? 0.5 : 1,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.04)',
                            },
                            }}
                            onClick={
                                item.disabled
                                    ? undefined
                                    : item.action || (() => !item.external && handleNavigation(item.path))
                                }>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                >
                                {item.icon}
                            </Box>
                            <Typography
                                variant="body2"
                                fontWeight={isActive ? 700 : 600}
                                sx={{ color: textColor }}
                                >
                                {item.label}
                            </Typography>
                        </Box>
                    );

                    return item.external ? (
                        <Link
                            key={item.label}
                            href={item.path}
                            underline="none"
                            color="inherit"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleClose}
                        >
                            {content}
                        </Link>
                        ) : (
                        <Box key={item.label}>{content}</Box>
                        );
                    })}
                </Stack>

                </Popover>
            </Box>

            {loadingStripePortal && <Loader />}
        </>
    );
}